import React, { useState, useEffect, useRef } from 'react';

/**
 * HeroMedia — полноэкранный фоновый медиа-слой для главного экрана.
 * Задерживает первый кадр (постер) не менее чем на 3 секунды,
 * полностью исключает проскакивание черного экрана при переходе к видео,
 * отслеживая фактический старт рендеринга видеокадров GPU.
 */
const isReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const MIN_POSTER_DURATION_MS = 3000; // Минимальная задержка первого кадра — 3 секунды

export const HeroMedia = ({
  videoUrl,
  webmUrl,
  poster,
  mobileImage,
  overlayClassName = 'bg-black/30',
  className = '',
  ...rest
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoFrameReady, setIsVideoFrameReady] = useState(false);
  const [isAutoplayBlocked, setIsAutoplayBlocked] = useState(false);
  const [isPosterTimerDone, setIsPosterTimerDone] = useState(false);

  const posterSrc = poster || mobileImage || '';
  const canShowVideo = Boolean(videoUrl) && !isReducedMotion();

  // Сброс всех состояний при смене URL видео
  useEffect(() => {
    setIsPlaying(false);
    setIsVideoFrameReady(false);
    setIsAutoplayBlocked(false);
    setIsPosterTimerDone(false);
  }, [videoUrl]);

  // Запуск таймера удержания первого кадра (3 секунды)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPosterTimerDone(true);
    }, MIN_POSTER_DURATION_MS);

    return () => clearTimeout(timer);
  }, [videoUrl]);

  // Управление стартом видео строго с 0-й секунды по истечении 3 секунд
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !canShowVideo) return;

    let isMounted = true;

    // Гарантируем отключенный звук для политик автоплея браузеров
    video.muted = true;
    video.defaultMuted = true;

    const startVideoFromBeginning = () => {
      if (!isMounted) return;

      try {
        video.currentTime = 0;
      } catch (e) {
        /* fallback */
      }

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (isMounted) {
              setIsPlaying(true);
              setIsAutoplayBlocked(false);
            }
          })
          .catch((err) => {
            console.warn('[HeroMedia] Autoplay blocked by Low Power Mode or policy:', err);
            if (isMounted) {
              setIsAutoplayBlocked(true);
            }
          });
      }
    };

    if (isPosterTimerDone) {
      startVideoFromBeginning();
    } else {
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* fallback */
      }
    }

    // Возобновление при первой активности пользователя
    const handleUserInteraction = () => {
      if (!isMounted || !video) return;
      if (video.paused && isPosterTimerDone) {
        try {
          video.currentTime = 0;
        } catch (e) {}
        video.play()
          .then(() => {
            if (isMounted) {
              setIsPlaying(true);
              setIsAutoplayBlocked(false);
            }
            cleanupListeners();
          })
          .catch(() => {});
      }
    };

    const cleanupListeners = () => {
      window.removeEventListener('touchstart', handleUserInteraction);
      window.removeEventListener('click', handleUserInteraction);
      window.removeEventListener('scroll', handleUserInteraction);
    };

    window.addEventListener('touchstart', handleUserInteraction, { passive: true });
    window.addEventListener('click', handleUserInteraction, { passive: true });
    window.addEventListener('scroll', handleUserInteraction, { passive: true });

    return () => {
      isMounted = false;
      cleanupListeners();
    };
  }, [videoUrl, canShowVideo, isPosterTimerDone]);

  // Фиксация момента, когда видео реально начало рендерить видеоряд на экране
  const handleTimeUpdate = () => {
    if (videoRef.current && videoRef.current.currentTime > 0.05) {
      setIsVideoFrameReady(true);
    }
  };

  return (
    <div className={`absolute inset-0 overflow-hidden bg-transparent ${className}`} {...rest}>
      {/* Слой 1: Статичный постер (удерживается до полной отрисовки первого кадра видео) */}
      {posterSrc && (
        <img
          src={posterSrc}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-1000 ${
            isPlaying && isVideoFrameReady && !isAutoplayBlocked ? 'opacity-0' : 'opacity-100'
          }`}
          loading="eager"
          decoding="async"
        />
      )}

      {/* Слой 2: Видео-поток (проявляется ТОЛЬКО когда видеокадр готов) */}
      {canShowVideo && (
        <video
          ref={videoRef}
          onTimeUpdate={handleTimeUpdate}
          onPlaying={() => {
            if (videoRef.current && videoRef.current.currentTime > 0.05) {
              setIsVideoFrameReady(true);
            }
          }}
          style={{
            opacity: isPlaying && isVideoFrameReady && !isAutoplayBlocked ? 1 : 0,
            transition: 'opacity 1.2s ease-in-out'
          }}
          loop
          muted
          playsInline
          webkit-playsinline="true"
          preload="auto"
          poster={posterSrc || undefined}
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          className={`absolute inset-0 w-full h-full object-cover z-10 ${
            isAutoplayBlocked ? 'pointer-events-none hidden' : 'pointer-events-none'
          }`}
        >
          {webmUrl && <source src={webmUrl} type="video/webm" />}
          <source src={videoUrl} type="video/mp4" />
        </video>
      )}

      {/* Слой 3: Затемняющий оверлей для контрастности */}
      <div className={`absolute inset-0 z-20 pointer-events-none ${overlayClassName}`} />
    </div>
  );
};

export default HeroMedia;
